/* @HEADER
 * @version 1.1.1375 | 2026-10-31
 * @purpose ZVX-DEV-8269 — moves a window that stands on a project of an organisation the person was removed from to another project (the personal one first) and sets the one banner; acts on the pointer, so a second call finds nothing.
 * @sidecar moveOffLeftOrganisation.ts.header.md
 * @layer app | @hex adapter | @ctx project
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-5305, ZVX-DEV-8269

import { organisationsLeft, readKnownOrganisations, type LeftOrganisation } from '../auth/organisationMemory.ts'
import { forgetToldExcept, markTold, readTold } from '../auth/leftOrganisationTold.ts'
import { resolveStoredAuthUserId } from '../auth/resolveStoredAuthUserId.ts'
import { readAccountIdentity } from '../features/account/public-api'
import type { MoveHost } from './moveOffRemovedProject.ts'
import { organisationIdOf } from './localProjectOwnership.ts'
import {
  getActiveProjectId,
  getAllProjects,
  getAllProjectsIncludingPendingDelete,
  type ProjectRecord,
} from './projectStore.ts'

/** What the move needs from the host: the switch, and the one banner the host keeps for the life of the window. */
export type LeftOrganisationMoveHost = Pick<MoveHost, 'switchProject' | 'deletedNotice'>

export interface LeftOrganisationMovePlan {
  organisation: LeftOrganisation
  /** The project to stand on instead, or null when the device holds no other project the person may see. */
  targetId: string | null
}

export interface LeftOrganisationMoveInput {
  /** The project this window's pointer names, from the list that still holds the hidden rows. Undefined when it names none. */
  pointerProject: Pick<ProjectRecord, 'id' | 'organizationType' | 'organizationId'> | undefined
  left: readonly LeftOrganisation[]
  /** The projects that are visible to this person now, in registry order: the hidden ones are not among them. */
  visible: readonly Pick<ProjectRecord, 'id' | 'systemKind'>[]
}

/**
 * ZVX-DEV-8269 -- is the window standing on a project of an organisation the person was removed from, and where does it go?
 * A window on any other project, the personal one included, is not moved and told nothing: the lists change under it and that is all.
 * The person's own project is preferred as the place to land (the same preference as the move off a deleted project); with none,
 * the first visible one.
 */
export function planMoveOffLeftOrganisation(input: LeftOrganisationMoveInput): LeftOrganisationMovePlan | null {
  const { pointerProject, left, visible } = input
  if (!pointerProject) return null
  // A project with no organisation names none: `find` then matches nothing and the window is left alone.
  const organisationId = organisationIdOf(pointerProject)
  const organisation = left.find((o) => o.id === organisationId)
  if (!organisation) return null
  const target = visible.find((p) => p.systemKind === 'personal-home') ?? visible[0]
  return { organisation, targetId: target?.id ?? null }
}

/**
 * Tells the person once for each organisation they were removed from, wherever this window stands, and moves the window off a
 * project that has just been hidden. The notice and the told-mark are settled before this returns, so a caller with nothing to
 * move (`null`) carries on in the same tick. The move acts on the pointer, not on a record of past events: once the window has
 * moved there is nothing left to act on, so a second call finds nothing; the banner is guarded by the told-mark instead.
 */
export function startMoveOffLeftOrganisation(host: LeftOrganisationMoveHost): Promise<void> | null {
  const userId = resolveStoredAuthUserId()
  if (userId === null) return null
  let storage: Pick<Storage, 'getItem' | 'setItem'> | null = null
  try {
    storage = typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    storage = null
  }
  const left = organisationsLeft(readKnownOrganisations(storage, userId), readAccountIdentity().workspaces)
  // An organisation that is no longer left (the person is back in it) loses its mark, so a second removal is told again.
  forgetToldExcept(storage, userId, left.map((o) => o.id))
  if (left.length === 0) return null
  const pointerId = getActiveProjectId()
  const visible = getAllProjects()
  const plan = planMoveOffLeftOrganisation({
    pointerProject: getAllProjectsIncludingPendingDelete().find((p) => p.id === pointerId),
    left,
    visible,
  })
  const told = readTold(storage, userId)
  const untold = left.filter((o) => told[o.id] === undefined)
  // One banner for however many organisations, and it NAMES every one it marks as told: «told» means the name was heard (ZVX-5305).
  // The one the window stands in comes first, the rest in the order the device remembers them. An organisation with no name is
  // counted, so the sentence still says how many there were.
  const stands = untold.find((o) => o.id === plan?.organisation.id)
  const ordered = stands ? [stands, ...untold.filter((o) => o !== stands)] : untold
  if (ordered.length > 0 && markTold(storage, userId, ordered.map((o) => o.id), new Date().toISOString())) {
    const names = ordered.map((o) => o.name).filter((name) => name.length > 0)
    host.deletedNotice.set({
      reason: 'left-organisation',
      removedName: null,
      openedName: null,
      organisationName: names[0] ?? null,
      organisationNames: names,
      unnamedCount: ordered.length - names.length,
    })
  }
  if (!plan) return null
  const next = visible.find((p) => p.id === plan.targetId) ?? null
  return Promise.resolve(next ? host.switchProject(next as ProjectRecord) : undefined)
}
